import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Invoice, ApiResponse, PricingItem } from '@/types'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import { leaseKeys } from './useLeases'
import { accountSettingsKeys } from './useAccountSettings'

// Query keys
export const invoiceKeys = {
  all: ['invoices'] as const,
  lists: () => [...invoiceKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...invoiceKeys.lists(), filters] as const,
  details: () => [...invoiceKeys.all, 'detail'] as const,
  detail: (id: number) => [...invoiceKeys.details(), id] as const,
}

// Fetch invoices
// EP-97: Invoices is a RENT-ROLL page — the index defaults to the latest billing
// period that has rows and sorts by unit number asc unless the caller overrides.
// `opts.allPeriods` sends `all_periods=true` to escape the period default (embedded
// consumers that need the customer's/lease's full history pass this); `opts.sort` /
// `opts.sort_order` drive the user-selectable server-side column sort.
export function useInvoices(
  page = 1,
  status?: string,
  leaseId?: number,
  from?: string,
  to?: string,
  customerId?: number,
  q?: string,
  propertyId?: number,
  opts: { sort?: string; sort_order?: string; allPeriods?: boolean } = {},
) {
  return useQuery({
    queryKey: invoiceKeys.list({ page, status, leaseId, from, to, customerId, q, propertyId, ...opts }),
    queryFn: async () => {
      const params: Record<string, unknown> = { page }
      if (status) params.status = status
      if (leaseId) params.lease_id = leaseId
      if (from) params.from = from
      if (to) params.to = to
      if (customerId) params.customer_id = customerId
      if (q) params.q = q
      if (propertyId) params.property_id = propertyId
      if (opts.sort) params.sort = opts.sort
      if (opts.sort_order) params.sort_order = opts.sort_order
      if (opts.allPeriods) params.all_periods = true
      const { data } = await api.get<ApiResponse<Invoice[]>>('/api/invoices', { params })
      return data
    },
  })
}

// Fetch single invoice
export function useInvoice(id: number) {
  return useQuery({
    queryKey: invoiceKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Invoice>>(`/api/invoices/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

// Imperative fetch for use in event handlers
export async function fetchInvoice(id: number): Promise<Invoice> {
  const { data } = await api.get<ApiResponse<Invoice>>(`/api/invoices/${id}`)
  return data.data
}

// Create invoice
interface CreateInvoiceInput {
  lease_id?: number | null
  customer_id?: number | null
  billed_at: string
  due_at: string
  billing_period: string
  total: number
  items?: {
    item_type: string
    description: string
    quantity: number
    unit_price: number
    amount: number
  }[]
}

export function useCreateInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateInvoiceInput) => {
      const { data } = await api.post<ApiResponse<Invoice>>('/api/invoices', {
        invoice: input,
      })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, invoiceKeys.lists())
      // Lease balance changes when an invoice is created — invalidate lease detail
      // so any cached lease summary (balance_due, invoices count) reflects the new state.
      if (data.lease_id) {
        invalidateDetailQueries(queryClient, leaseKeys.detail(data.lease_id))
      }
    },
  })
}

// Update invoice
interface UpdateInvoiceInput {
  id: number
  billed_at?: string
  due_at?: string
  billing_period?: string
  total?: number
  items?: {
    item_type: string
    description: string
    quantity: number
    unit_price: number
    amount: number
  }[]
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateInvoiceInput) => {
      const { data } = await api.patch<ApiResponse<Invoice>>(`/api/invoices/${id}`, {
        invoice: input,
      })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, invoiceKeys.lists())
      invalidateDetailQueries(queryClient, invoiceKeys.detail(data.id))
    },
  })
}

// Delete invoice
export function useDeleteInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/invoices/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, invoiceKeys.lists())
      // Refresh per-entity counts so empty-state seed/affordance gating is accurate.
      queryClient.invalidateQueries({ queryKey: accountSettingsKeys.all })
    },
  })
}

// Payments
export const paymentKeys = {
  all: ['payments'] as const,
  lists: () => [...paymentKeys.all, 'list'] as const,
  list: (invoiceId: number) => [...paymentKeys.lists(), invoiceId] as const,
}

interface CreatePaymentInput {
  invoiceId: number
  amount: number
  payment_method: 'cash' | 'transfer' | 'promptpay'
  paid_at: string
  reference?: string
  slip_url?: string
}

export function useCreatePayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ invoiceId, ...input }: CreatePaymentInput) => {
      const { data } = await api.post<ApiResponse<unknown>>(
        `/api/invoices/${invoiceId}/payments`,
        { payment: input }
      )
      return { data: data.data, invoiceId }
    },
    onSuccess: (result) => {
      invalidateDetailQueries(queryClient, invoiceKeys.detail(result.invoiceId))
      removeListQueries(queryClient, invoiceKeys.lists())
    },
  })
}

// Price check for a single lease
interface PriceCheckItem {
  item_type: string
  description: string
  quantity: number
  unit_price: number
  amount: number
}

// The resolved engine inputs returned alongside the priced items, so the
// client can re-price an inline quantity edit through POST /api/pricing_calculation
// instead of replicating the engine in JS (see InvoiceFields).
export interface PriceCheckContext {
  pricing_formula: PricingItem[]
  unit_price: number
  consumption: Record<string, number>
  unit_area_sqm: number | null
  unit_share_ratio: number | null
}

interface PriceCheckResponse {
  data: {
    lease_id: number
    items: PriceCheckItem[]
    total: number
    missing_readings: string[]
    pricing_context: PriceCheckContext
  }
}

export function useInvoicePriceCheck() {
  return useMutation({
    mutationFn: async ({ leaseId, billingPeriod }: { leaseId: number; billingPeriod: string }) => {
      const { data } = await api.post<PriceCheckResponse>(`/api/leases/${leaseId}/invoice_items`, null, {
        params: { billing_period: billingPeriod },
      })
      return data.data
    },
  })
}

// Batch preview
interface InvoicePreviewItem {
  lease_id: number
  lease_code: string
  customer_name: string | null
  unit_name: string | null
  /** Partial-period markers from the server; date-only ISO values for display. */
  move_in_date?: string | null
  move_out_date?: string | null
  items: { item_type: string; description: string; quantity: number; unit_price: number; amount: number }[]
  total: number
  missing_readings: string[]
  negative_usage?: string[]
  /** Present when an invoice for this lease already exists in the period — the
   *  batch generate step skips these so re-running Generate is idempotent. */
  existing_invoice?: { id: number; number: string; total: number } | null
  /** EP-149 T149-015 (MON-8): the due date this row's invoice would get if
   *  generated right now, including any explicit override, derived server-side
   *  from the lease's property `payment_due_day` (or issue date + 15 days
   *  when unset). Date-only 'YYYY-MM-DD'. */
  due_at: string
  /** Whether `due_at` has already passed, compared against the account's own
   *  timezone (not UTC). */
  past_due: boolean
  /** The date a real late-fee run would start charging from. Present only on
   *  a past-due row with late fees configured for the account and the
   *  lease's unit; null otherwise. */
  late_fee_starts_at: string | null
}

interface InvoicePreviewResponse {
  data: InvoicePreviewItem[]
  summary: {
    total_leases: number
    total_amount: number
    missing_readings_count: number
    already_invoiced_count?: number
    /** True when at least one row in the batch is already past due. */
    due_date_warning: boolean
  }
}

export function useInvoicePreview() {
  return useMutation({
    mutationFn: async ({ billingPeriod, dueAt }: { billingPeriod: string; dueAt?: string }) => {
      const { data } = await api.post<InvoicePreviewResponse>('/api/invoices/preview', {
        billing_period: billingPeriod,
        ...(dueAt ? { due_at: dueAt } : {}),
      })
      return data
    },
  })
}

interface GenerateInvoicesInput {
  billingPeriod: string
  /** EP-149 T149-016 (MON-8): omitted (undefined) lets each invoice take its
   *  own lease's property due date (backend per-lease default, T149-015).
   *  When present, overrides the whole batch to this one date, exactly as
   *  before T149-015. */
  dueAt?: string
  /** When present (including empty array), restricts generation to the given
   *  lease IDs only. When omitted (undefined), all eligible leases are processed
   *  (backend "ABSENT = all eligible" semantics). */
  leaseIds?: number[]
}

export function useGenerateInvoices() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ billingPeriod, dueAt, leaseIds }: GenerateInvoicesInput) => {
      const body: Record<string, unknown> = {
        billing_period: billingPeriod,
      }
      // Only include due_at when the caller explicitly overrides it: omitting
      // the key lets the backend derive each lease's own due date.
      if (dueAt) {
        body.due_at = dueAt
      }
      // Only include lease_ids when the caller explicitly passes it (even as []).
      // Omitting the key preserves the backend "ABSENT = all eligible" semantics.
      if (leaseIds !== undefined) {
        body.lease_ids = leaseIds
      }
      const { data } = await api.post<{ data: Invoice[]; meta: { count: number } }>('/api/invoices/generate', body)
      return data
    },
    onSuccess: () => {
      removeListQueries(queryClient, invoiceKeys.lists())
    },
  })
}

export function useDeletePayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, invoiceId }: { id: number; invoiceId: number }) => {
      await api.delete(`/api/payments/${id}`)
      return { id, invoiceId }
    },
    onSuccess: (result) => {
      invalidateDetailQueries(queryClient, invoiceKeys.detail(result.invoiceId))
      removeListQueries(queryClient, invoiceKeys.lists())
    },
  })
}

export function useSignInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, signed_pdf_key }: { id: number; signed_pdf_key: string }) => {
      const { data } = await api.post<ApiResponse<Invoice>>(`/api/invoices/${id}/sign`, { signed_pdf_key })
      return data.data
    },
    onSuccess: (data) => {
      invalidateDetailQueries(queryClient, invoiceKeys.detail(data.id))
      removeListQueries(queryClient, invoiceKeys.lists())
    },
  })
}
